import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { QueryFetchFunction, QueryTransformer } from 'common/helpers/common';
import {
  ApiError,
  ApiResult,
  ExtendedApiResult,
} from 'common/repositories/common.model';

import { SaleLiteModel } from './common';
import {
  getSaleInput,
  getSalesInput,
  getTransactionalFootnoteInput,
  SaleDetailModel,
  SaleMinimalModel,
  TransactionalFootnoteModel,
} from './model';

export function useGetSales(
  input?: getSalesInput,
  options?: UseQueryOptions<ExtendedApiResult<SaleMinimalModel[]>, ApiError>,
): UseQueryResult<ExtendedApiResult<SaleMinimalModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['getSales', input?.params],
      () =>
        QueryFetchFunction({
          url: 'sales',
          params: input?.params,
        }),
      options,
    ),
    SaleMinimalModel,
  );
}

export function useGetRefundSales(
  input?: getSalesInput,
  options?: UseQueryOptions<ExtendedApiResult<SaleLiteModel[]>, ApiError>,
): UseQueryResult<ExtendedApiResult<SaleLiteModel[]>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-refund-sales', input?.params],
      () =>
        QueryFetchFunction({
          url: 'sales/get-refund-sales',
          params: input?.params,
        }),
      options,
    ),
    SaleLiteModel,
  );
}

export function useGetDetailSale(
  input?: getSaleInput,
  options?: UseQueryOptions<ApiResult<SaleDetailModel>, ApiError>,
): UseQueryResult<ApiResult<SaleDetailModel>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-sale-detail', input?.id],
      () => QueryFetchFunction({ url: `sales/get-sales/${input?.id}` }),
      options,
    ),
    SaleDetailModel,
  );
}

export function useGetTransactionableFootnote(
  type: 'sales' | 'purchases' = 'sales',
  input: getTransactionalFootnoteInput,
  options?: UseQueryOptions<
    ApiResult<TransactionalFootnoteModel | null>,
    ApiError
  >,
): UseQueryResult<ApiResult<TransactionalFootnoteModel | null>, ApiError> {
  return QueryTransformer(
    useQuery(
      options?.queryKey ?? ['get-transactional-footnote', input?.id],
      () =>
        QueryFetchFunction({
          url: `${type}/${input?.id}/transactional-footnote`,
        }),
      options,
    ),
    TransactionalFootnoteModel,
  );
}

export function getSaleDetailKey(input?: getSaleInput) {
  return ['get-sale-detail', input?.id];
}

export function getSaleKey(input?: getSaleInput) {
  return ['getSale', input?.id];
}

export function getRefundSalesKey(input?: getSalesInput) {
  return ['get-refund-sales', input?.params];
}

export function getTransactionalFootnoteKey(
  input: getTransactionalFootnoteInput,
) {
  return ['get-transactional-footnote', input?.id];
}
